import axios from 'axios';
import {Modal} from 'antd';
import {ExclamationCircleFilled} from '@ant-design/icons';
import React from 'react';
import {getMessageApi} from '../utils/messageHolder';
import {isPublicSitePath, stripSiteLocaleFromPathname} from '../i18n/siteLocalePath';

const API_BASE_URL = '';

const TOKEN_KEY = 'auth_token';

// 标记是否已显示过踢下线弹窗（避免重复弹窗）
let hasShownKickedModal = false;

export const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    // Axios 1.x 默认 indexes: false → 数组序列化为 key[]=v1&key[]=v2
    // Spring Boot @RequestParam List<X> 无法解析 [] 后缀，需设为 indexes: null
    // 使数组序列化为 key=v1&key=v2 格式
    paramsSerializer: {
        indexes: null,
    },
});

// 从localStorage获取token
const getToken = (): string | null => {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
};

// 清除token
const clearToken = (): void => {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('auth_user');
        document.cookie = 'auth_token=; path=/; max-age=0';
    } catch {
        // ignore
    }
};

// 请求拦截器 - 添加token到Authorization header
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
// 鉴权未通过的请求现在返回 HTTP 200 + ApiResponse(code: "UNAUTHORIZED"/"SINGLE_LOGIN_KICKED"/"TOKEN_VERSION_CHANGED"/"403")
// 因此需要在 success handler 中检查响应体的 code 字段做特化处理
api.interceptors.response.use(
    (response) => {
        // 检查业务层面的 success 字段
        if (response.data && response.data.success === false) {
            const code = response.data.code;
            const errorMessage = response.data.message || '请求失败';

            // 1. 被踢下线
            if ('SINGLE_LOGIN_KICKED' === code && !hasShownKickedModal) {
                hasShownKickedModal = true;

                clearToken();
                delete api.defaults.headers.common['Authorization'];

                Modal.confirm({
                    type: 'warning',
                    icon: null,
                    title: React.createElement('div', {
                            style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 18,
                                fontWeight: 'bold',
                                color: 'var(--pt-amber)'
                            }
                        },
                        React.createElement(ExclamationCircleFilled, {
                            style: {
                                color: 'var(--pt-amber)',
                                marginRight: 8
                            }
                        }),
                        '账号下线通知'
                    ),
                    content: React.createElement('div', {
                        style: {
                            textAlign: 'center',
                            padding: '20px 0',
                            fontSize: 16,
                            color: '#fff',
                            lineHeight: 1.8
                        }
                    }, '您的账号已在其他设备登录', React.createElement('br'), '请重新登录'),
                    centered: true,
                    okText: '确定',
                    okButtonProps: {
                        danger: true,
                        size: 'large' as const,
                    },
                    cancelButtonProps: {
                        style: {display: 'none'},
                    },
                    width: 420,
                    maskClosable: false,
                    onOk: () => {
                        hasShownKickedModal = false;
                        window.location.href = '/login';
                    },
                });

                return Promise.reject(new Error(errorMessage));
            }

            // 2. 权限已变更
            if ('TOKEN_VERSION_CHANGED' === code) {
                clearToken();
                delete api.defaults.headers.common['Authorization'];
                Modal.warning({
                    title: '权限已变更',
                    content: '您的权限已被管理员修改，请重新登录',
                    centered: true,
                    okText: '确定',
                    okButtonProps: {danger: true, size: 'large' as const},
                    width: 420,
                    maskClosable: false,
                    onOk: () => {
                        window.location.href = '/login';
                    },
                });

                return Promise.reject(new Error(errorMessage));
            }

            // 3. 普通鉴权未通过（token过期等）
            // 已在登录页时不跳转，避免 LoginPage 轮询 /users/me 时触发整页刷新循环
            if ('UNAUTHORIZED' === code) {
                // /users/me 是「探测登录态」的静默接口（登录页/官网轮询用），
                // 未登录返回 UNAUTHORIZED 属正常结果，不应清 token 或整页跳转登录页
                const reqUrl = response.config?.url || '';
                const isProbeMe = reqUrl.includes('/users/me');
                // 官网公开页面（首页/官网各页/登录注册等）会复用部分登录态组件，
                // 其受保护请求在未登录时返回 UNAUTHORIZED 属预期，不能把访客整页踢到登录页
                const isPublicSite = isPublicSitePath(stripSiteLocaleFromPathname(window.location.pathname));
                if (!isProbeMe) {
                    clearToken();
                    delete api.defaults.headers.common['Authorization'];
                }
                if (!isProbeMe && !isPublicSite && '/login' !== window.location.pathname) {
                    getMessageApi()?.error('登录已过期，请重新登录');
                    window.location.href = '/login';
                }

                return Promise.reject(new Error(errorMessage));
            }

            // 4. 其他业务错误（含 403 权限不足等）
            return Promise.reject(new Error(errorMessage));
        }
        return response;
    },
    (error) => {
        // 标记服务端失联：网络层错误（无响应）或 5xx 服务端错误（如后端不可达/代理转发失败）
        if (!error.response || error.response.status >= 500) {
            error.__networkError = true;
        }
        // 非2xx HTTP 响应（网络异常、500等）
        if (error.response) {
            const data = error.response.data;
            if (data && (data.code !== '0' || data.success === false)) {
                const errorMessage = data?.message || data?.msg || '请求失败';
                return Promise.reject(new Error(errorMessage));
            }
        }
        return Promise.reject(error);
    }
);

export default api;
